import { useMemo, useRef, useState } from 'react';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  Switch,
  IconButton,
} from '@mui/material';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import moment from 'moment';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { useTranslation } from 'react-i18next';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import NotesIcon from '@mui/icons-material/Notes';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import './fonts/OpenSans-Regular-normal';
import './fonts/OpenSans-SemiBold-normal';

import { useAppDispatch, useAppSelector } from 'store';
import { clearCallStore, selectCall } from 'store/call';
import { selectUser } from 'store/user';
import { getFullName, getIntlLocale, parseIdentity } from 'utils';

import styles from './NotesDialog.module.scss';

export interface NotesDialogProps extends DialogProps {
  onConfirm: () => void;
  onClose?: () => void;
  participantNamesOverride?: string[];
}

export function NotesDialog({ onConfirm, onClose, participantNamesOverride, ...rest }: NotesDialogProps) {
  const { t, i18n } = useTranslation('settings');
  const locale = getIntlLocale(i18n.resolvedLanguage || i18n.language || 'en');
  const dispatch = useAppDispatch();
  const call = useAppSelector(selectCall);
  const user = useAppSelector(selectUser);
  const sessionNotesRef = useRef<any>(null);

  const [dontShowAgain, setDontShowAgain] = useState(localStorage.getItem('hideNotesDialog') === 'true');

  const generatePDF = async () => {
    const content = sessionNotesRef.current as HTMLDivElement | null;
    const dialogPaper = content?.closest('.MuiDialog-paper') as HTMLDivElement | null;
    if (!content || !dialogPaper) return;
    const exportHost = document.createElement('div');
    exportHost.className = styles.pdfExportHost;
    const exportContent = dialogPaper.cloneNode(true) as HTMLDivElement;
    exportContent.classList.add(styles.pdfExportContent);
    exportContent.querySelector(`.${styles.summaryCloseIcon}`)?.remove();
    exportContent.querySelector(`.${styles.actionsRow}`)?.remove();
    exportContent.querySelector(`.${styles.exportMetaHeader}`)?.remove();
    exportHost.appendChild(exportContent);
    document.body.appendChild(exportHost);

    try {
      const dialogWidth = Math.max(520, Math.ceil(dialogPaper.getBoundingClientRect().width));
      exportContent.style.width = `${dialogWidth}px`;

      const canvas = await html2canvas(exportContent, {
        scale: 2,
        useCORS: true,
        backgroundColor: null,
        width: exportContent.scrollWidth,
        height: exportContent.scrollHeight,
        windowWidth: 1400,
        windowHeight: exportContent.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });

      const imageData = canvas.toDataURL('image/png');
      const doc = new jsPDF('p', 'pt', 'a4');

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const renderWidth = pageWidth - margin * 2;
      const renderHeight = (canvas.height * renderWidth) / canvas.width;
      const pageContentHeight = pageHeight - margin * 2;
      doc.addImage(imageData, 'PNG', margin, margin, renderWidth, renderHeight);

      let remainingHeight = renderHeight - pageContentHeight;
      while (remainingHeight > 0) {
        doc.addPage();
        const yOffset = margin - (renderHeight - remainingHeight);
        doc.addImage(imageData, 'PNG', margin, yOffset, renderWidth, renderHeight);
        remainingHeight -= pageContentHeight;
      }

      doc.save('notes.pdf');
    } catch (error) {
      const doc = new jsPDF('p', 'pt', 'a4');
      doc.setFont('OpenSans-Regular', 'normal');
      await doc.html(exportContent, {
        margin: [20, 0, 20, 60],
        html2canvas: { windowWidth: 1400, scale: 1.5, backgroundColor: null },
      });
      doc.save('notes.pdf');
      console.warn('html2canvas export failed; fallback export used.', error);
    } finally {
      document.body.removeChild(exportHost);
    }

    dispatch(clearCallStore());
    onConfirm();
  };

  const handleClose = () => {
    dispatch(clearCallStore());
    onClose && onClose();
  };

  const getTimezoneAbbreviation = (date: moment.Moment, locale: string) => {
    const nativeDate = date.toDate();
    const parts = new Intl.DateTimeFormat(locale, { timeZoneName: 'short' }).formatToParts(nativeDate);
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? 'Local';
  };

  const formatLocalizedDate = (date: moment.Moment, locale: string) =>
    new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date.toDate());

  const formatLocalizedTime = (date: moment.Moment, locale: string) =>
    new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date.toDate());

  const [startDate, startDateString, startTimeString] = useMemo(() => {
    let date = moment().utc();
    if (call?.startDate) {
      date = moment.utc(call?.startDate, 'YYYY-MM-DDTHH:mm:ss.SSSSZ');
    }
    const localDate = date.local();
    const timezone = getTimezoneAbbreviation(localDate, locale);

    return [localDate, formatLocalizedDate(localDate, locale), `${formatLocalizedTime(localDate, locale)} ${timezone}`];
  }, [call?.startDate, i18n.language, i18n.resolvedLanguage]);

  const [endDate, endTimeString] = useMemo(() => {
    let date = moment.utc();
    if (call?.endDate && call?.endDate >= call?.startDate) {
      date = moment.utc(call?.endDate, 'YYYY-MM-DDTHH:mm:ss.SSSSZ');
    }
    const localDate = date.local();
    const timezone = getTimezoneAbbreviation(localDate, locale);
    return [localDate, `${formatLocalizedTime(localDate, locale)} ${timezone}`];
  }, [call?.endDate, call?.startDate, i18n.language, i18n.resolvedLanguage]);

  const duration = useMemo(() => {
    const totalSeconds = Math.max(0, endDate.diff(startDate, 'seconds'));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}${t('callHistory.minutesShort')} ${seconds}${t('callHistory.secondsShort')}`;
  }, [endDate, startDate, t]);

  const formatClockDuration = (secondsValue?: number | null) => {
    if (secondsValue == null) return '--:--';
    const safe = Math.max(0, secondsValue);
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const getWaitColor = (waitTimeSeconds?: number | null) => ((waitTimeSeconds ?? 0) < 300 ? '#1DAA5A' : '#E53935');

  const hostName = useMemo(() => {
    return (call?.hostName || getFullName(user) || t('callHistory.host')).trim();
  }, [call?.hostName, user, t]);

  const organizationName = useMemo(() => {
    return user?.clinicUser.organization?.trim() || '—';
  }, [user?.clinicUser.organization]);

  const hostUrl = useMemo(() => {
    const roomName = user?.clinicUser.roomName?.trim();
    if (!roomName) return '—';
    const configuredPublicUri = process.env.REACT_APP_PUBLIC_URI?.trim();
    const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const baseUrl = (configuredPublicUri || fallbackOrigin).replace(/\/+$/, '');
    return `${baseUrl}/${roomName}`;
  }, [user?.clinicUser.roomName]);

  const hostDurationSeconds = useMemo(() => {
    return Math.max(0, endDate.diff(startDate, 'seconds'));
  }, [endDate, startDate]);

  const displayParticipants = useMemo(() => {
    const detailed = call?.participantDetails ?? [];
    const normalizedHost = hostName.toLowerCase();
    const fallbackRealNames = (participantNamesOverride?.length ? participantNamesOverride : call?.participants ?? [])
      .map((identityString: string) => {
        if (participantNamesOverride?.length) {
          return (identityString ?? '').trim();
        }

        const [fallbackIdentity, displayName] = parseIdentity(identityString ?? '');
        return (displayName || fallbackIdentity || '').trim();
      })
      .filter((name: string) => Boolean(name) && name.toLowerCase() !== normalizedHost);
    let fallbackNameIndex = 0;
    const guestLabelPattern = /^Guest\s+\d+$/i;

    if (detailed.length > 0) {
      return detailed
        .filter(
          (p: { name: string; waitTimeSeconds?: number | null; participationTimeSeconds?: number | null }) =>
            p.name?.trim().toLowerCase() !== normalizedHost,
        )
        .map((p: { name: string; waitTimeSeconds?: number | null; participationTimeSeconds?: number | null }) => ({
          name:
            p.name && guestLabelPattern.test(p.name.trim()) && fallbackNameIndex < fallbackRealNames.length
              ? fallbackRealNames[fallbackNameIndex++]
              : p.name,
          waitTimeSeconds: p.waitTimeSeconds ?? null,
          participationTimeSeconds: p.participationTimeSeconds ?? null,
        }));
    }

    return (call?.participants ?? [])
      .map((identityString: string) => {
        const [, displayName] = parseIdentity(identityString ?? '');
        const fallback = parseIdentity(identityString ?? '')[0] ?? '';
        return (displayName || fallback).trim();
      })
      .filter((name) => Boolean(name) && name.toLowerCase() !== normalizedHost)
      .map((name) => ({
        name,
        waitTimeSeconds: null as number | null,
        participationTimeSeconds: null as number | null,
      }));
  }, [call?.participantDetails, call?.participants, hostName, participantNamesOverride]);

  return (
    <Dialog {...rest} className={styles.dialog}>
      <DialogTitle className={styles.summaryHeader}>
        <div className={styles.summaryHeaderLeft}>
          <span className={styles.summaryHeaderIcon}>
            <PhoneIcon fontSize='small' />
          </span>
          <Typography component='p' variant='h2'>
            {t('callHistory.callSummary')}
          </Typography>
        </div>
        <IconButton size='small' className={styles.summaryCloseIcon} onClick={handleClose}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>
      <DialogContent ref={sessionNotesRef} className={styles.summaryDialogContent}>
        <div className={styles.summaryBody}>
          <div className={styles.exportMetaHeader}>
            <Typography className={styles.exportMetaLine}>
              <strong>{t('callHistory.summaryOrganization')}:</strong> {organizationName}
            </Typography>
            <Typography className={styles.exportMetaLine}>
              <strong>{t('callHistory.summaryHost')}:</strong> {hostName}
            </Typography>
            <Typography className={styles.exportMetaLine}>
              <strong>{t('callHistory.summaryHostUrl')}:</strong> {hostUrl}
            </Typography>
          </div>

          <div className={styles.summaryPanel}>
            <Typography component='p' className={styles.summarySectionTitle}>
              <CalendarMonthIcon fontSize='small' />
              {t('callHistory.sessionDetails')}
            </Typography>
            <div className={styles.summaryDetailsLayout}>
              <div className={styles.summaryDetailsGrid}>
                <Typography className={styles.summaryLabel}>{t('callHistory.summaryDate')}:</Typography>
                <Typography className={styles.summaryValue}>{startDateString}</Typography>
                <Typography className={styles.summaryLabel}>{t('callHistory.summaryStartTime')}:</Typography>
                <Typography className={styles.summaryValue}>{startTimeString}</Typography>
                <Typography className={styles.summaryLabel}>{t('callHistory.summaryEndTime')}:</Typography>
                <Typography className={styles.summaryValue}>{endTimeString}</Typography>
              </div>
              <div className={styles.summaryDurationCard}>
                <Typography className={styles.summaryDurationLabel}>
                  {t('callHistory.summaryTotalDuration')}:
                </Typography>
                <Typography className={styles.summaryDurationValue}>{duration}</Typography>
              </div>
            </div>
          </div>

          <div className={styles.summaryPanel}>
            <Typography component='p' className={styles.summarySectionTitle}>
              <GroupsIcon fontSize='small' />
              {t('callHistory.participants')}
            </Typography>
            <div className={styles.summaryParticipantsScroller}>
              <div className={styles.summaryParticipantsTable}>
                <div className={styles.summaryParticipantsHeader}>
                  <span>{t('callHistory.participant')}</span>
                  <span>{t('callHistory.waitTime')}</span>
                  <span>{t('callHistory.duration')}</span>
                  <span>{t('callHistory.role')}</span>
                </div>

                <div className={styles.summaryParticipantRow}>
                  <span className={styles.summaryParticipantName}>
                    <PersonIcon fontSize='small' />
                    {hostName}
                  </span>
                  <span className={styles.summaryWaitBadge}>—</span>
                  <span>{formatClockDuration(hostDurationSeconds)}</span>
                  <span>{t('callHistory.host')}</span>
                </div>

                {displayParticipants.map((participant, index: number) => (
                  <div key={`${participant.name}-${index}`} className={styles.summaryParticipantRow}>
                    <span className={styles.summaryParticipantName}>
                      <PersonIcon fontSize='small' />
                      {participant.name}
                    </span>
                    <span
                      className={styles.summaryWaitBadge}
                      style={{
                        color: getWaitColor(participant.waitTimeSeconds),
                        borderColor: getWaitColor(participant.waitTimeSeconds),
                        backgroundColor: `${getWaitColor(participant.waitTimeSeconds)}14`,
                      }}
                    >
                      {participant.waitTimeSeconds != null ? formatClockDuration(participant.waitTimeSeconds) : '--'}
                    </span>
                    <span>{formatClockDuration(participant.participationTimeSeconds)}</span>
                    <span>{t('callHistory.guest')}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className={styles.summaryPanel}>
            <Typography component='p' className={styles.summarySectionTitle}>
              <NotesIcon fontSize='small' />
              {t('callHistory.sessionNotes')}
            </Typography>
            <Typography component='pre' className={styles.notes}>
              {call?.meetingNotes || t('callHistory.noNotesEntered')}
            </Typography>
          </div>
        </div>
      </DialogContent>
      <DialogActions className={styles.actionsRow}>
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          justifyContent='space-between'
          width='100%'
          className={styles.actionsContent}
        >
          <Stack direction='row' spacing={1} alignItems='center' className={styles.actionsToggleRow}>
            <Typography component='p' variant='body5'>
              {t('callHistory.doNotShowAgain')}
            </Typography>
            <Switch
              checked={dontShowAgain}
              onChange={(e) => {
                const value = e.target.checked;
                setDontShowAgain(value);
                localStorage.setItem('hideNotesDialog', value.toString());
              }}
            />
          </Stack>
          <Stack direction='row' spacing={1} className={styles.actionsButtonsRow}>
            <Button variant='outlined' onClick={handleClose}>
              {t('callHistory.close')}
            </Button>
            <Button variant='contained' onClick={generatePDF}>
              {t('callHistory.exportPdf')}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
